import Head from 'next/head';
import { useState, Fragment } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { ReactSVG } from 'react-svg';
import BlockParser from '~/components/blockParser/blockParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import InPageError from '~/components/inPageError/inPageError';
import Search from '~/components/search/search';
import { truncate, getBlocksFromQueue, getIcon } from '~/utils/utils';
import SiteSettings from '~/siteSettings';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

/**
 * Search
 * @category Page
 * @extends Component
 * @summary Display the search results page.
 * @return {Node} React node containing the search results page.
 */

const SearchPage = props => {
  const { baseUrl, term, type, data, page, blocks } = props;
  const [activeFilter, setActiveFilter] = useState(type);
  const router = useRouter();
  const filters = [
    {
      key: 'all',
      copy: 'see all'
    },
    {
      key: 'recipe',
      copy: 'recipes'
    },
    {
      key: 'video',
      copy: 'videos'
    },
    {
      key: 'gallery',
      copy: 'galleries'
    },
    {
      key: 'article',
      copy: 'articles'
    }
  ];

  const setFilter = e => {
    if(e?.currentTarget?.dataset?.filter) {
      const filter = e.currentTarget.dataset.filter;
      setTimeout(() => {
        router.push(`/search?q=${ term }&t=${ filter }`);
      },100);
    }
  }

  const boldResult = text => {
    const regEx = new RegExp(`(${ term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') })`, 'ig');
    return text.replace(regEx, '<strong>$1</strong>');
  }

  if(process.env.NEXT_PUBLIC_ENABLE_SEARCH === '1') {
    return (
      <>
        <Head>
          <title>Search | The Drew Barrymore Show</title>
          <link rel='canonical' href={`${ baseUrl }/search`} />
          <meta name='description' content='Search' />
        </Head>
        <div className='search__header'>
          <div className='constrain'>
            <h1>we get to discover together</h1>
            <Search theme='page' term={ term } />
          </div>
        </div>
        <div className='constrain'>
          <nav className='search__filters'>
            <ul>
              {filters.map((filter) => (
                <li
                  className={ activeFilter === filter.key ? 'search__filters-active' : '' }
                  key={`filter-${ filter.key }`}
                  onClick={ setFilter }
                  data-filter={ filter.key }
                >
                  <button>
                    <span>{ filter.copy }</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div className='search__count'>
            { data?.pager?.total_items } results for <strong>&ldquo;{ term }&rdquo;</strong>
          </div>
          { (data?.rows?.length >= 1) ?
            <>
              {data?.rows?.map?.((result, i) => (
                <Fragment key={`filter-${ result?.title?.replaceAll?.(' ', '_') }`}>
                  <div className='search__result'>
                    <div>
                      <a href={ result.link } className='search__result-photo'>
                        <picture>
                          <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ result.img_sm } />
                          <img
                            src={ result.img_lg }
                            alt={ result.title }
                            loading='lazy'
                            className='search__result-photo-img'
                          />
                        </picture>
                        <picture>
                          <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ result.img_sm } />
                          <img
                            src={ result.img_lg }
                            alt={ result.title }
                            loading='lazy'
                            className='search__result-photo-guide'
                          />
                        </picture>
                        <div className='search__item-icon'>
                          <ReactSVG src={ getIcon(result.type) } />
                        </div>
                      </a>
                    </div>
                    <div className='search__result-copy'>
                      <a href={ result.link }>
                        <h2>{ result.title }</h2>
                      </a><br />
                      <a className='search__result-category' href={ result.category_url } dangerouslySetInnerHTML={{__html: result.category}} />
                      <div className='search__result-copy-desc' dangerouslySetInnerHTML={{__html: boldResult(truncate(`${ result.date } - ${ result.desc }`, 220))}} />
                    </div>
                  </div>
                  {(i+1) % 6 == 0 &&
                    <div className='search__ad'>
                      <div
                        data-ad-ref='leader-plus-flex-inc'
                        data-incremental={ i }
                        className='bidbarrel-ad'
                      ></div>
                      <div
                        data-ad-ref='mobile-plus-flex-inc'
                        data-incremental={ i }
                        className='bidbarrel-ad'
                      ></div>
                    </div>
                  }
                </Fragment>
              ))}
              { parseInt(data?.pager?.total_pages, 10) >= 1 &&
                <div className='search__pagination-wrap'>
                  <nav className='search__pagination'>
                    <div>
                      <Link href={`/search?q=${ term }&t=${ type }&p=${ parseInt(page, 10) === 0 ? (data?.pager?.total_pages - 1) : parseInt(page, 10) - 1 }`} passHref>
                        <a href='#' className='search__pagination-transport'>
                          <ReactSVG src='/img/icons/arrow_pager.svg' />
                        </a>
                      </Link>
                    </div>
                    <ul>
                      { [...Array(parseInt(data?.pager?.total_pages, 10))].map((item, i) => (
                        <li key={`filter-${ i }`}>
                          <Link href={`/search?q=${ term }&t=${ type }&p=${ i }`} passHref>
                            <a className={ i === parseInt(page, 10) ? 'search__pagination-active' : '' }>
                              <span>{ i + 1 }</span>
                            </a>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div>
                      <Link href={`/search?q=${ term }&t=${ type }&p=${ parseInt(page, 10) === (data?.pager?.total_pages - 1) ? 0 : parseInt(page, 10) + 1 }`} passHref>
                        <a href='#' className='search__pagination-transport search__pagination-transport--next'>
                          <ReactSVG src='/img/icons/arrow_pager.svg' />
                        </a>
                      </Link>
                    </div>
                  </nav>
                </div>
              }
            </>
            :
            <>
              <div className='search__notice'>
                Sorry we didn&apos;t find what you were looking for, check out some of our most recent content below!
              </div>
              { blocks?.entitySubQueueQuery?.items &&
                <BlockParser blockItems={ blocks.entitySubQueueQuery.items } />
              }
            </>
          }
        </div>
        <BidBarrelReact
          mapToAdUnits={{
            mobile: [
              'mobile-plus-flex-inc'
            ],
            desktop: [
              'leader-plus-flex-inc'
            ]
          }}
        />
        <Utag
          pageType='search'
          siteSection='search'
          data={{ searchTerm: term }}
        />
      </>
    );
  }

  return(
    <InPageError />
  );
}

export async function getServerSideProps(ctx) {
  // Construct URL
  const {q,p=0,t='all'} = ctx?.query;
  const url = new URL(`${ process.env.NEXT_PUBLIC_FILE_BASE_URL }/api/solr`);
  let blocks = {};
  url.searchParams.append('_format', 'json');
  url.searchParams.append('offset', parseInt(p, 10) * 12);
  url.searchParams.append('search_api_fulltext', q);
  if(t && t !== 'all') {
    url.searchParams.append('type', t);
  }

  // Call Search
  let data = {};
  const dataReq = await fetch(url.href, { method: 'GET' });
  if(dataReq.ok) {
    data = await dataReq.json();
  }

  // Get Blocks
  if(!data?.rows) {
    blocks = await getBlocksFromQueue({
      target_id: 'search_blocks',
      limit: 100
    });
  }

  return {
    props: {
      term: q,
      type: t,
      page: p,
      data,
      blocks
    }
  }
}

export default PageWrapper(SearchPage);
